import { motion, useReducedMotion } from 'framer-motion'
import { Search } from 'lucide-react'

/**
 * WhoWeAre — "Nuestro equipo estará esperándote" (mockup oficial FITLAB 19/may/2026).
 *
 * Bloque de equipo real con 5 miembros ordenados por jerarquía: cada miembro muestra
 * área de responsabilidad sobre la foto + chip con nombre y profesión debajo.
 *
 * Las 5 fotos viven en `/public/brand/team-*.jpg` (480×480 q85 ~25 KB c/u).
 */

type TeamMember = {
  src: string
  area: string
  name: string
  role: string
  alt: string
}

const TEAM: TeamMember[] = [
  {
    src: '/brand/team-medico-director.jpg',
    area: 'Dirección Médica',
    name: 'Juan Manuel Oliva',
    role: 'Médico de Salud Ocupacional',
    alt: 'Médico Director de Salud Ocupacional FITLAB',
  },
  {
    src: '/brand/team-ing-civil-industrial.jpg',
    area: 'Dirección ejecutiva y operaciones',
    name: 'María Díaz Gallardo',
    role: 'Ing. Civil Industrial',
    alt: 'Ingeniera Civil Industrial del equipo FITLAB',
  },
  {
    src: '/brand/team-medicos-asesores.jpg',
    area: 'Equipo médico asesor',
    name: 'Médicos colaboradores',
    role: 'en salud ocupacional y evaluación integral',
    alt: 'Equipo de médicos asesores FITLAB',
  },
  {
    src: '/brand/team-ing-civil-matematico.jpg',
    area: 'Dirección de análisis estadístico',
    name: 'Felipe Jara Z.',
    role: 'Ing. Civil Matemático',
    alt: 'Ingeniero Civil Matemático del equipo FITLAB',
  },
  {
    src: '/brand/team-ing-informatico.jpg',
    area: 'Encargado innovación y tecnología',
    name: 'Javier Gómez O.',
    role: 'Ing. Informático',
    alt: 'Ingeniero Informático del equipo FITLAB',
  },
]

export default function WhoWeAre() {
  const reduced = useReducedMotion()

  return (
    <section
      id="nosotros"
      className="relative overflow-hidden bg-white py-24 sm:py-32"
      aria-label="Nuestro equipo FITLAB"
    >
      {/* Halo decorativo sutil tricolor */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-32 left-1/2 h-[500px] w-[500px] -translate-x-1/2 rounded-full opacity-25 blur-3xl"
        style={{
          background:
            'radial-gradient(circle, rgba(43, 191, 122, 0.5) 0%, rgba(30, 95, 201, 0.3) 40%, transparent 70%)',
        }}
      />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Sub-badge SOMOS FITLAB en la cabecera */}
        <motion.div
          initial={reduced ? undefined : { opacity: 0, scale: 0.9 }}
          whileInView={reduced ? undefined : { opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.6 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="flex justify-center"
        >
          <span className="inline-flex items-center gap-2 rounded-full border border-fitlab-neutral-100 bg-white px-4 py-1.5 text-xs font-bold uppercase tracking-[0.22em] text-fitlab-blue-hover shadow-sm">
            <Search
              className="h-3.5 w-3.5 text-fitlab-green-hover"
              strokeWidth={2.5}
              aria-hidden="true"
            />
            Somos <span className="text-fitlab-gradient">FITLAB</span>
          </span>
        </motion.div>

        {/* Heading principal — split en 2 líneas con énfasis en "esperándote" */}
        <motion.h2
          initial={reduced ? undefined : { opacity: 0, y: 14 }}
          whileInView={reduced ? undefined : { opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.55, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          className="mx-auto mt-6 max-w-3xl text-balance text-center text-3xl font-extrabold leading-tight tracking-tight text-fitlab-blue-hover sm:text-4xl lg:text-5xl"
        >
          Nuestro equipo estará{' '}
          <motion.span
            initial={reduced ? undefined : { opacity: 0, scale: 0.92 }}
            whileInView={reduced ? undefined : { opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{
              duration: 0.6,
              delay: 0.35,
              ease: [0.16, 1, 0.3, 1],
            }}
            className="inline-block bg-fitlab-gradient bg-clip-text font-display italic text-transparent"
            style={{ paddingBottom: '0.05em' }}
          >
            esperándote
          </motion.span>
        </motion.h2>

        <motion.p
          initial={reduced ? undefined : { opacity: 0, y: 12 }}
          whileInView={reduced ? undefined : { opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.55, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className="mx-auto mt-5 max-w-2xl text-center text-base leading-relaxed text-fitlab-neutral-600 sm:text-lg"
        >
          Equipo conformado por especialistas con experiencia clínica y gestión empresarial, listos
          para acompañar a tu empresa en cada etapa.
        </motion.p>

        {/* Grid 5 fotos circulares */}
        <ul
          role="list"
          className="mx-auto mt-14 grid max-w-5xl grid-cols-2 gap-x-6 gap-y-10 sm:grid-cols-3 sm:gap-x-8 lg:grid-cols-5 lg:gap-x-6"
        >
          {TEAM.map((m, i) => (
            <motion.li
              key={m.name}
              initial={reduced ? undefined : { opacity: 0, scale: 0.92, y: 16 }}
              whileInView={reduced ? undefined : { opacity: 1, scale: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{
                duration: 0.5,
                delay: reduced ? 0 : i * 0.08,
                ease: [0.16, 1, 0.3, 1],
              }}
              whileHover={reduced ? undefined : { y: -4 }}
              className="group flex flex-col items-center text-center"
            >
              {/* Área de responsabilidad — sub-título sobre la foto (slot
                  con altura fija para alinear los círculos a la misma Y
                  cuando los títulos ocupan 1, 2 o 3 líneas) */}
              <div className="mb-3 flex min-h-[3rem] max-w-[11rem] items-end justify-center sm:min-h-[3.5rem]">
                <p className="text-center text-[0.65rem] font-bold uppercase leading-tight tracking-[0.14em] text-fitlab-blue-hover sm:text-xs">
                  {m.area}
                </p>
              </div>

              {/* Avatar circular con ring tricolor */}
              <div className="relative">
                <span
                  aria-hidden="true"
                  className="absolute inset-0 rounded-full bg-fitlab-gradient opacity-50 blur-md transition-opacity group-hover:opacity-80"
                />
                <span className="relative inline-block rounded-full bg-fitlab-gradient p-[3px] shadow-md">
                  <img
                    src={m.src}
                    alt={m.alt}
                    loading="lazy"
                    decoding="async"
                    width={176}
                    height={176}
                    className="block h-36 w-36 rounded-full object-cover ring-2 ring-white sm:h-40 sm:w-40 lg:h-36 lg:w-36 xl:h-44 xl:w-44"
                  />
                </span>
              </div>

              {/* Chip con nombre + profesión */}
              <div className="mt-4 max-w-[11rem] rounded-2xl bg-fitlab-neutral-50 px-3 py-2 shadow-sm ring-1 ring-fitlab-neutral-100">
                <p className="text-xs font-bold leading-snug text-fitlab-blue-hover sm:text-sm">
                  {m.name}
                </p>
                <p className="mt-1 text-[0.7rem] font-medium leading-snug text-fitlab-neutral-600 sm:text-xs">
                  {m.role}
                </p>
              </div>
            </motion.li>
          ))}
        </ul>
      </div>
    </section>
  )
}
